<script setup lang="ts">
    import { Permissions } from '@shared/Root/Permissions';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import SidebarMenu, { SidebarMenuGroup } from '@shared/components/SidebarMenu.vue';
    import Loading from '@shared/components/Loading.vue';
    import { computed, inject } from 'vue';
    import { useRoute } from 'vue-router';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { ScopeAccess } from '@shared/directives/Scope.access';
    import { FeatureService } from '@shared/directives/Feature.service';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);
    const featureService: FeatureService = injectorAccess.injector.get(FeatureService);
    const route = useRoute();

    const menuGroups: SidebarMenuGroup[] = [
        {
            heading: $Resources.AccountTabHeadingUsers,
            items: [
                { routeNames: ['UserProfile'], label: $Resources.UserProfileTab },
                {
                    routeNames: ['AccountUsers'],
                    label: $Resources.AccountUsersTab,
                    requireScope: { Feature: 'Account', Permission: Permissions.Read },
                },
            ],
        },
        {
            heading: $Resources.AccountTabHeadingCurrentAccount,
            items: [
                {
                    routeNames: ['AccountInfo'],
                    label: $Resources.AccountInfoTabSettings,
                    requireScope: { Feature: 'Account', Permission: Permissions.Read },
                },
                {
                    routeNames: ['AccountGeneralSettings'],
                    label: $Resources.AccountTabSettings,
                    requireScope: { Feature: 'Account', Permission: Permissions.Write },
                },
                {
                    routeNames: ['AccountMailSettings'],
                    label: $Resources.AccountTabMailSettings,
                    requireScope: { Feature: 'Account', Permission: Permissions.Write },
                },
                {
                    routeNames: ['SubscriptionCategories'],
                    label: $Resources.AccountPageTabsAccountSubscriptions,
                    requireScope: { Feature: 'Account', Permission: Permissions.Write },
                },
                {
                    routeNames: ['AccountTagManager'],
                    label: $Resources.TagManagerTab,
                    requireScope: { Feature: 'Tags', Permission: Permissions.Write },
                },
                { routeNames: ['AccountThemes'], label: $Resources.ThemesListHeading },
            ],
        },
        {
            heading: $Resources.AccountTabHeadingPayments,
            items: [
                {
                    routeNames: ['AccountSubscription'],
                    label: $Resources.CompanyModalServiceSubscriptionTab,
                    requireScope: { Feature: 'Account', Permission: Permissions.Read },
                },
                {
                    routeNames: ['AccountBillingDetails'],
                    label: $Resources.SubscriptionAndBillingBillingDetails,
                    requireScope: { Feature: 'Account', Permission: Permissions.Read },
                },
                {
                    routeNames: ['AccountInvoices'],
                    label: $Resources.InvoicesTab,
                    requireScope: { Feature: 'Account', Permission: Permissions.Read },
                },
                {
                    routeNames: ['AccountCreditCards'],
                    label: $Resources.CreditCardsTab,
                    requireScope: { Feature: 'Account', Permission: Permissions.Read },
                },
                {
                    routeNames: ['AccountTransactions'],
                    label: $Resources.TransactionsTab,
                    requireScope: { Feature: 'Account', Permission: Permissions.Read },
                },
                {
                    // No Account scope (SMS top-up on invoice, R1–R3): whoever may pay — an administrator or "Betalningsbehörig" —
                    // tops up here, and the balance and the offer are Any Read in the Api. Others see the balance and are told
                    // they may not pay (the page reads CanPay).
                    routeNames: ['AccountPrePaidBalance'],
                    label: $Resources.SubscriptionAndBillingPrePaidBalanceTab,
                },
            ],
        },
        {
            heading: $Resources.AccountTabHeadingOther,
            items: [
                {
                    routeNames: ['AccountApiKeys'],
                    label: $Resources.ApiKeysMenuLabel,
                    requireScope: { Feature: 'Account', Permission: Permissions.Read },
                },
                {
                    routeNames: ['AccountConnections'],
                    label: $Resources.ConnectionsMenuLabel,
                    requireScope: { Feature: 'Account', Permission: Permissions.Read },
                    feature: 'ConnectionTab',
                },
                {
                    routeNames: ['AccountDataManagement'],
                    label: $Resources.DataManagementHeading,
                    requireScope: { Feature: 'Account', Permission: Permissions.Write },
                },
                {
                    routeNames: ['AccountProcessingTasks'],
                    label: $Resources.ProcessingTasksHeading,
                    requireScope: { Feature: 'Account', Permission: Permissions.Read },
                },
            ],
        },
    ];

    // The menu hides items the user can't access, but the routes are still reachable directly - apply the
    // same requirements here so a disallowed child view never mounts (and never calls its API).
    const canViewCurrentRoute = computed<boolean | null>(() => {
        if (!contextAccess.state.currentUser) return null;

        const routeName = route.name?.toString() ?? '';
        const item = menuGroups.flatMap((g) => g.items).find((i) => i.routeNames.includes(routeName));
        if (!item) return true;

        const hasScope = !item.requireScope || scopeAccess.hasScope(item.requireScope);
        const hasFeature = !item.feature || featureService.hasFeature(item.feature);

        return hasScope && hasFeature;
    });

    const newVersionAlertContent = () => {
        const url = `/AccountOld`;
        return $Resources.GeneralNewVersionAlertContent.replace('{{', `<a href="${url}">`).replace('}}', '</a>');
    };
</script>

<template>
    <div class="has-sidebar">
        <nav role="navigation" class="sidebar set-height custom-scrollbar navbar-sticky">
            <SidebarMenu :groups="menuGroups" />
        </nav>

        <section>
            <BsAlert class="m-sm-t" :type="AlertTypes.Info" :title="$Resources.GeneralNewVersionAlertTitle" :content="newVersionAlertContent()" />
            <router-view v-if="canViewCurrentRoute === true"></router-view>
            <BsAlert
                v-else-if="canViewCurrentRoute === false"
                class="m-sm-t"
                :type="AlertTypes.Warning"
                :title="$Resources.UnauthorizedPageHeading"
                :content="$Resources.UnauthorizedPageText"
                :dismissible="false" />
            <Loading v-else :show="true" loading-style="progress-bar" fixed></Loading>
        </section>
    </div>
</template>

